// @flow
import React, {useCallback, useMemo, useState} from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {PAGE_SEARCH_RESULTS} from "../../../../locales/pages/namespaces";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {NumericFormat} from "react-number-format";
import useUserData from "../../../../hooks/user/useUserData";
import {applyPercentage, roundToDecimal} from "../../../../helpers/helpers";
import {ClearOutlined} from "@mui/icons-material";

type Props = {
    open: boolean,
    handleDialog: Function
};

const HalfCalculator = (props: Props) => {
    const {open, handleDialog} = props
    const {thousandSeparator, decimalSeparator, data} = useUserData()
    const {t} = useTranslation(PAGE_SEARCH_RESULTS)
    const [extraDiscPct, setExtraDiscPct] = useState('');
    const asPct = (v) => {
        if (v === '' || v == null) return 0;
        const n = Math.trunc(Number(v));
        return Math.max(0, Math.min(99, Number.isFinite(n) ? n : 0));
    };

    const handleClose = (evt, details) => {
        if (details === 'backdropClick') return
        setPieces(1)
        setEarnNet(0)
        setEarnPercentage(0)
        setExtraDiscPct('');
        handleDialog({
            open: false,
            prices: null
        })
    }
    const [pieces, setPieces] = useState(1)
    const [earnNet, setEarnNet] = useState(0)
    const [earnPercentage, setEarnPercentage] = useState(0)

    const extraPct = asPct(extraDiscPct);

    const priceListValue = useMemo(() => (
        pieces > 0
            ? roundToDecimal(
                pieces * Number(open.prices?.priceAfterDiscounts ?? 0) * (1 - extraPct/100),
                2
            )
            : '-'
    ), [pieces, open.prices?.priceAfterDiscounts, extraPct]);

    const priceListValueWithTax = useMemo(() => (
        pieces > 0
            ? roundToDecimal(
                pieces * Number(open.prices?.priceAfterDiscountsWithTax ?? 0) * (1 - extraPct/100),
                2
            )
            : '-'
    ), [pieces, open.prices?.priceAfterDiscountsWithTax, extraPct]);

    const finalCost = useMemo(() => (
        pieces > 0
            ? roundToDecimal(
                pieces * Number(open.prices?.priceWithPaymentDiscountWithEnvTax ?? 0) * (1 - extraPct/100),
                2
            )
            : '-'
    ), [pieces, open.prices?.priceWithPaymentDiscountWithEnvTax, extraPct]);

    const finalCostWithVat = useMemo(() => (
        pieces > 0
            ? roundToDecimal(
                pieces * Number(open.prices?.priceWithPaymentDiscountWithEnvTaxAndItemTax ?? 0) * (1 - extraPct/100),
                2
            )
            : '-'
    ), [pieces, open.prices?.priceWithPaymentDiscountWithEnvTaxAndItemTax, extraPct]);

    const clearCalculator = useCallback(() => {
        setPieces(1)
        setEarnNet(0)
        setEarnPercentage(0)
        setExtraDiscPct('');
    }, [])

    return (
        <Dialog open={open.open} onClose={handleClose} maxWidth={'xs'}>
            <DialogTitle>
                <Typography
                    variant={'h5'}
                    component={'div'}
                    sx={{textAlign: 'center'}}
                >
                    {t('Prices Calculations')}
                    <br/>
                    {open.prices?.description || ' - '}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography
                    variant={'h6'}
                    component={'div'}
                    sx={{fontWeight: 'bold', textAlign: 'center', color: '#FFFFFF', backgroundColor: '#000000'}}
                    m={0.5}
                >
                    {t('Prices Per Piece')}
                </Typography>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell
                                sx={{textAlign: 'right', backgroundColor: '#f0f8ff'}}>{t('Without Tax')} (€)</TableCell>
                            <TableCell
                                sx={{textAlign: 'right', backgroundColor: '#f0f8ff'}}>{t('With Tax')} (€)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell
                                sx={{paddingLeft: 0.5, backgroundColor: 'secondary.light'}}>{t('Wholesale')}</TableCell>
                            <TableCell sx={{textAlign: 'right'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={open.prices?.priceWithoutTax}
                                />
                            </TableCell>
                            <TableCell sx={{textAlign: 'right'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={open.prices?.priceWithTax}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{
                                paddingLeft: 0.5,
                                backgroundColor: 'secondary.light'
                            }}>{t('On Invoice')}</TableCell>
                            <TableCell sx={{textAlign: 'right'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={open.prices?.priceAfterDiscounts}
                                />
                            </TableCell>
                            <TableCell sx={{textAlign: 'right'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={open.prices?.priceAfterDiscountsWithTax}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{paddingLeft: 0.5, backgroundColor: 'secondary.light', maxWidth: '140px'}}>
                                {t('Final Cost (payment discount & env. tax')}
                            </TableCell>
                            <TableCell sx={{textAlign: 'right'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={open.prices?.priceWithPaymentDiscountWithEnvTax}
                                />
                            </TableCell>
                            <TableCell sx={{textAlign: 'right'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={open.prices?.priceWithPaymentDiscountWithEnvTaxAndItemTax}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <Box
                    m={1.1}
                    sx={{textAlign: 'center'}}
                >
                    <NumericFormat
                        thousandSeparator={thousandSeparator}
                        decimalSeparator={decimalSeparator}
                        value={pieces}
                        onValueChange={(values, sourceInfo) => {
                            setPieces(values.floatValue)
                        }}
                        decimalScale={0}
                        customInput={TextField}
                        size={'small'}
                        InputLabelProps={{shrink: true}}
                        variant={"outlined"}
                        label={t('Pieces')}
                    />
                    <Tooltip title={t('Clear')}>
                        <Button
                            variant="text"
                            color={'error'}
                            onClick={clearCalculator}
                            size={'small'}
                        >
                            <ClearOutlined fontSize={'small'}/>
                        </Button>
                    </Tooltip>
                </Box>
                <Typography
                    variant={'h6'}
                    component={'div'}
                    sx={{fontWeight: 'bold', textAlign: 'center', color: '#FFFFFF', backgroundColor: '#000000'}}
                    m={1}
                >
                    {t('Calculated Price')}
                </Typography>
                <Box
                    m={1.1}
                    sx={{textAlign: 'center',mt:2}}
                >
                    <NumericFormat
                        thousandSeparator={thousandSeparator}
                        decimalSeparator={decimalSeparator}
                        value={extraDiscPct}
                        onValueChange={(values, sourceInfo) => {
                            if (sourceInfo?.source === 'prop') return;
                            if (values.value === '' || values.floatValue == null) {
                                setExtraDiscPct('');
                                return;
                            }
                            const clean = Math.max(0, Math.min(99, Math.trunc(values.floatValue)));
                            setExtraDiscPct(String(clean));
                        }}
                        decimalScale={0}
                        allowNegative={false}
                        isAllowed={(vals) => (vals.floatValue == null || vals.floatValue <= 99)}
                        allowEmptyFormatting
                        customInput={TextField}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        label={t('With Extra Discount')}
                        name="extraDiscPct"
                        style={{ width: 160 }}
                    />
                    <span>%</span>

                    <Tooltip title={t('Clear')}>
                        <Button
                            variant="text"
                            color={'error'}
                            onClick={clearCalculator}
                            size={'small'}
                        >
                            <ClearOutlined fontSize={'small'}/>
                        </Button>
                    </Tooltip>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell
                                sx={{textAlign: 'right', backgroundColor: '#f0f8ff'}}>{t('Without Tax')} (€)</TableCell>
                            <TableCell
                                sx={{textAlign: 'right', backgroundColor: '#f0f8ff'}}>{t('With Tax')} (€)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{
                                paddingLeft: 0.5,
                                backgroundColor: 'secondary.light'
                            }}>{t('On Invoice')}</TableCell>
                            <TableCell sx={{textAlign: 'right'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={priceListValue}
                                />
                            </TableCell>
                            <TableCell sx={{textAlign: 'right'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={priceListValueWithTax}
                                />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{
                                paddingLeft: 0.5,
                                backgroundColor: 'secondary.light',
                                maxWidth: '140px'
                            }}>{t('Final Cost (payment discount & env. tax')}</TableCell>
                            <TableCell sx={{textAlign: 'right', fontWeight: 'bold'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={finalCost}
                                />
                            </TableCell>
                            <TableCell sx={{textAlign: 'right', fontWeight: 'bold'}}>
                                <NumericFormat
                                    thousandSeparator={thousandSeparator}
                                    decimalSeparator={decimalSeparator}
                                    displayType="text"
                                    value={finalCostWithVat}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button
                    color="error"
                    size={"small"}
                    onClick={handleClose}
                    sx={{
                        marginRight: 'auto'
                    }}
                >
                    {t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HalfCalculator